// Phase v1.3 (MUI-05 / D-COACH-01) — on-demand Hint button.
//
// Small Sun-Yellow icon button rendered in MatchTopBar's `hintSlot` (Plan
// Task 6 wires the slot prop). Visible only while `realMatchesPlayed < 5`
// (D-COACH-01 gate; server enforces the same in D-COACH-09).
//
// Tap → calls the consumer-supplied `onClick` (MatchScreen wires this to
// `request()` from useCoachHint). Disabled while a request is in-flight
// (consumer passes `loading`).

import type { JSX } from 'react'
import { useIntl } from 'react-intl'

import { Btn } from '../components/Btn'
import { PowerIcon } from '../components/PowerIcon'

export interface HintButtonProps {
  /** Server-tracked counter. Button hides when >= 5 (D-COACH-01). */
  readonly realMatchesPlayed: number
  /** Fires on tap. Consumer wires this to `useCoachHint().request`. */
  readonly onClick: () => void
  /** Disables the button while a request is in-flight. */
  readonly loading?: boolean
}

export function HintButton({
  realMatchesPlayed,
  onClick,
  loading = false,
}: HintButtonProps): JSX.Element | null {
  const intl = useIntl()
  if (realMatchesPlayed >= 5) return null
  const ariaLabel = intl.formatMessage({ id: 'coach.hint.button_label' })
  // App-canonical hint glyph — PowerIcon name="whisper" (the same icon
  // CoachOverlay surfaces as the canonical hint icon).
  const iconTitle = intl.formatMessage({
    id: 'coach.hint.icon_title',
    defaultMessage: 'Hint',
  })
  return (
    <Btn
      kind="primary"
      size="sm"
      onClick={onClick}
      ariaLabel={ariaLabel}
      disabled={loading}
      testId="coach-hint-button"
    >
      <PowerIcon name="whisper" size={20} title={iconTitle} />
    </Btn>
  )
}
